import { useCreateOneComment } from "#src/components/hooks/adapters/useComments";
import { useGetOneWorkflowTemplate } from "#src/components/hooks/adapters/useWorkflowTemplates";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownInput,
  Form,
  RadioInput,
  TextAreaInput,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  CommentEntity,
  Resources,
  WorkflowAdapter,
  WorkflowSchema,
  WorkflowStatus,
  type WorkflowType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import React from "react";
import { z } from "zod";

const { WorkflowStatusOptions } = Workflows.view;

const statusDialogFormSchema = z.object({
  status: WorkflowSchema.shape.status,
  dismissal_reason: z.string().optional(),
  comment: z.string().optional(),
});
type StatusDialogFormType = z.infer<typeof statusDialogFormSchema>;

export const WorkflowDetailStatusDialog = ({
  setIsStatusModalOpen,
  isStatusModalOpen,
  workflowDetails,
}: {
  setIsStatusModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isStatusModalOpen: boolean;
  workflowDetails: WorkflowType;
}) => {
  const { id: workflowId, status, workflow_template_id } = workflowDetails;
  const form = useForm<StatusDialogFormType>({ defaultValues: { status } });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const selectedStatus = form.watch("status");
  const isDismissed = selectedStatus === WorkflowStatus.DISMISSED;

  const { data: workflowTemplateData, isLoading: isTemplateLoading } =
    useGetOneWorkflowTemplate(
      { id: workflow_template_id },
      { enabled: isStatusModalOpen && isDismissed }
    );
  const dismissalReasons = workflowTemplateData?.data?.dismissal_reasons ?? [];
  const hasDismissalReasons = dismissalReasons.length > 0;
  const dismissalReasonOptions = dismissalReasons.map((reason) => ({
    label: reason,
    value: reason,
  }));

  const { isLoading: isUpdatingStatus, mutateAsync: updateStatus } =
    useMutation({
      mutationFn: async (data: Partial<WorkflowType>) => {
        await WorkflowAdapter.updateOne({
          id: workflowId,
          data,
          previousData: workflowDetails,
        });
      },
    });

  const createCommentMutation = useCreateOneComment({ noAlerts: true });
  const isLoading =
    isUpdatingStatus ||
    createCommentMutation.isLoading ||
    (isDismissed && isTemplateLoading);

  const closeDialog = () => {
    form.reset();
    setIsStatusModalOpen(false);
  };

  const onFormSubmit = form.handleSubmit(async (values) => {
    if (isDismissed && isTemplateLoading) return;

    if (isDismissed && hasDismissalReasons && !values.dismissal_reason) {
      form.setError("dismissal_reason", {
        message: "A dismissal reason is required",
      });
      return;
    }

    const validatedStatus = WorkflowSchema.shape.status.safeParse(
      values.status
    );

    if (!validatedStatus.success) {
      toast.push({
        intent: "error",
        description: "Failed to update status. Please try again.",
      });
      return;
    }

    if (values.comment?.trim()) {
      try {
        await createCommentMutation.mutateAsync({
          data: { text: values.comment.trim(), files: [] },
          meta: {
            entity_type: CommentEntity.WORKFLOW,
            entity_id: workflowId,
            additional_identifier: "",
          },
        });
        queryClient.invalidateQueries({ queryKey: [Resources.COMMENT] });
      } catch (err) {
        console.error(err);
        toast.push({
          intent: "error",
          description:
            "Failed to save the comment. Status has not been changed. Please try again.",
        });
        return;
      }
    }

    try {
      await updateStatus({
        status: validatedStatus.data,
        ...(isDismissed && values.dismissal_reason
          ? { dismissal_reason: values.dismissal_reason }
          : {}),
      });
    } catch (err) {
      console.error(err);
      toast.push({
        intent: "error",
        description: "Unable to update workflow status",
      });
      return;
    }

    closeDialog();
    queryClient.invalidateQueries({ queryKey: [Resources.WORKFLOW] });
    toast.push({
      intent: "success",
      description: "Successfully updated workflow status",
    });
  });

  return (
    <Dialog
      isOpen={isStatusModalOpen}
      onClose={closeDialog}
      title="Change Status"
      actionRow={[
        <Button
          key="status-dialog-action"
          onClick={onFormSubmit}
          variant="primary"
          isLoading={isLoading}
        >
          Save
        </Button>,
      ]}
    >
      <Form {...form}>
        <RadioInput
          name="status"
          isRequired
          options={WorkflowStatusOptions}
          valueKey="value"
          labelKey="label"
        />
        {isDismissed && hasDismissalReasons ? (
          <DropdownInput
            name="dismissal_reason"
            label="Dismissal Reason"
            isRequired
            options={dismissalReasonOptions}
            labelKey="label"
            valueKey="value"
            isFluid
          />
        ) : null}
        {isDismissed && hasDismissalReasons ? (
          <TextAreaInput
            name="comment"
            label="Comment"
            placeholder="Add a comment about this dismissal..."
          />
        ) : null}
      </Form>
    </Dialog>
  );
};
